/**
 * Subscriber-side event personalization: the preference vocabulary, the
 * system defaults, resolution order (per-calendar ?? user default ?? system)
 * and the title formatter shared by the in-app preview and the ICS renderer.
 */
import { hasSportsTag } from "./sportsTags.js";
export const EVENT_PREFERENCE_KEYS = [
    "titleStyle",
    "myTeamFirst",
    "separator",
    "emoji",
    "whereToWatch",
    "spoilerSafe",
    "reminder",
];
/** Keys the feed render applies; any other key renders its system value and reports `not_yet_available`. */
export const RELEASED_EVENT_PREFERENCE_KEYS = Object.freeze(["emoji", "reminder"]);
/** `reason` codes a capability reports when a key is unsupported. */
export const CAPABILITY_REASONS = Object.freeze({
    /** The key isn't released yet. */
    notYetAvailable: "not_yet_available",
    /** The calendar's feed is never personalized (an external link or the elections feed). */
    notPersonalizable: "not_personalizable",
});
/** `Calendar.link` values whose feed Stanza renders; any other link is served from its origin, unpersonalized. */
export const PERSONALIZABLE_CALENDAR_LINKS = Object.freeze(["scraped", "scraped-legacy-manual"]);
export const TITLE_STYLES = ["full", "short"];
export const TITLE_SEPARATORS = ["vs", "at", "dash"];
export const REMINDER_OPTIONS = ["none", "30m", "2h", "1d"];
/** Text each separator token renders as inside a matchup title. */
export const SEPARATOR_TEXT = Object.freeze({
    vs: "vs",
    at: "@",
    dash: "–",
});
/** Minutes before the event start for each reminder option; `null` means no VALARM. */
export const REMINDER_MINUTES = Object.freeze({
    none: null,
    "30m": 30,
    "2h": 120,
    "1d": 1440,
});
/** Rendering with no preferences set; timed events alert 30 minutes before, all-day ones at 19:00 the day before. */
export const SYSTEM_EVENT_PREFERENCES = Object.freeze({
    titleStyle: "full",
    myTeamFirst: false,
    separator: "vs",
    emoji: null,
    whereToWatch: true,
    spoilerSafe: false,
    reminder: "30m",
});
/** Stored `emoji` value meaning "no emoji here", so a calendar can opt out of a default emoji (`null` inherits). */
export const NO_EMOJI = "";
const EMOJI_SEQUENCE = /(?:[0-9#*]\uFE0F?\u20E3|\p{Regional_Indicator}{2}|(?:(?=\p{Extended_Pictographic})\p{Emoji_Presentation}(?:\uFE0F|\p{Emoji_Modifier})?|\p{Extended_Pictographic}(?:\uFE0F|\p{Emoji_Modifier}))(?:[\u{E0020}-\u{E007E}]+\u{E007F})?(?:\u200D\p{Extended_Pictographic}(?:\uFE0F|\p{Emoji_Modifier})?)*)/u;
/**
 * Resolve the effective preferences for one calendar.
 * @param overrides - Per-calendar choices; `null` / missing keys inherit (`NO_EMOJI` is a choice, not a gap).
 * @param defaults - The user's defaults; `null` / missing keys fall back to the system value.
 * @param capabilities - Keys the calendar supports; an unsupported key always renders the system value.
 * @returns A complete preference set.
 */
export function resolveEventPreferences(overrides, defaults, capabilities) {
    const resolved = { ...SYSTEM_EVENT_PREFERENCES };
    for (const key of EVENT_PREFERENCE_KEYS) {
        if (capabilities && capabilities[key]?.supported !== true)
            continue;
        resolved[key] = overrides?.[key] ?? defaults?.[key] ?? SYSTEM_EVENT_PREFERENCES[key];
    }
    return resolved;
}
/**
 * Build an event title from its parts under the given preferences.
 * Matchups render away-then-home; "my team first" moves a home team to the
 * front and swaps `@` for `vs`, since `@` reads as away-at-home.
 * @param parts - A matchup (`home` + `away`) or a plain `title`.
 * @param prefs - Effective preferences for the calendar.
 * @returns The title the subscriber sees.
 */
export function formatPersonalizedTitle(parts, prefs) {
    const prefix = prefs.emoji ? `${prefs.emoji} ` : "";
    if (!parts.home || !parts.away)
        return `${prefix}${parts.title ?? ""}`;
    const label = (team) => prefs.titleStyle === "short" && team.short ? team.short : team.name;
    let first = parts.away;
    let second = parts.home;
    let separator = prefs.separator;
    if (prefs.myTeamFirst && parts.isMyTeamHome) {
        [first, second] = [second, first];
        if (separator === "at")
            separator = "vs";
    }
    return `${prefix}${label(first)} ${SEPARATOR_TEXT[separator]} ${label(second)}`;
}
/**
 * Pull the first complete emoji (including ZWJ, skin-tone and flag sequences) out of free text.
 * @param value - Raw input, e.g. what the OS emoji keyboard typed.
 * @returns The emoji, or `null` when the text has none.
 */
export function extractFirstEmoji(value) {
    if (!value)
        return null;
    return value.match(EMOJI_SEQUENCE)?.[0] ?? null;
}
/**
 * @param value - Candidate preference value.
 * @returns True when the value is exactly one emoji and nothing else.
 */
export function isSingleEmoji(value) {
    return Boolean(value) && extractFirstEmoji(value) === value;
}
/**
 * @param value - A stored or submitted `emoji` preference.
 * @returns True for `null` (inherit), `NO_EMOJI`, or exactly one emoji.
 */
export function isValidEmojiPreference(value) {
    return value === null || value === NO_EMOJI || (typeof value === "string" && isSingleEmoji(value));
}
/**
 * @param calendar - A calendar with its `tags` and `tvStatus`.
 * @returns The calendar's kind for personalization previews: sports, TV, or anything else.
 */
export function calendarKind(calendar) {
    if (hasSportsTag(calendar?.tags))
        return "sports";
    if (calendar?.tvStatus)
        return "tv";
    return "manual";
}
/**
 * @param link - A calendar's `link`.
 * @returns Whether Stanza renders that calendar's feed, so preferences can apply to it.
 */
export function isPersonalizableCalendarLink(link) {
    return typeof link === "string" && PERSONALIZABLE_CALENDAR_LINKS.includes(link);
}
/**
 * Mark every key outside `releasedKeys` unsupported (`not_yet_available`).
 * @param capabilities - Data-derived capabilities; released keys missing here count as supported.
 * @param releasedKeys - Defaults to `RELEASED_EVENT_PREFERENCE_KEYS`.
 * @returns A capability for every preference key.
 */
export function withReleasedKeys(capabilities = null, releasedKeys = RELEASED_EVENT_PREFERENCE_KEYS) {
    return Object.fromEntries(EVENT_PREFERENCE_KEYS.map((key) => [
        key,
        releasedKeys.includes(key)
            ? capabilities?.[key] ?? { supported: true }
            : { supported: false, reason: CAPABILITY_REASONS.notYetAvailable },
    ]));
}
//# sourceMappingURL=eventPersonalization.js.map